import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginationSorting } from '@shared/components/complex-table/complex-table.model';
import { HttpMethods } from '@cbre/http';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AvailabilitiesFilterOptions {
  availabilityType: string;
  status?: string;
  availabilitiesToggle?: string;
  needsReview?: boolean;
}

export interface AvailabilityCalculationItem {
  attribute_name: string;
  current_value: string | number;
  calculated_value: string | number;
  is_different?: boolean;
  enable_update?: boolean;
  queue_count?: boolean;
  is_highlight?: boolean;
}

@Injectable()
export class AvailabilitySpaceSyncService extends HttpMethods {
  refreshDiscrepancyCount$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Fetches all availabilities of certain usage for a property
   * @param recordId: property id
   * @param availabilityType: Property usage type
   * @param options: pagination params (limit & offset) & sorting params (sortField, sortFieldType & sortOrder)
   */
  getPropertyAvailabilityType(
    recordId: number,
    availabilitiesOptions: AvailabilitiesFilterOptions,
    paginationSortingOpts: PaginationSorting
  ): Observable<{ totalCount: number; results: any[] }> {
    const { availabilitiesToggle, availabilityType, status, needsReview } = availabilitiesOptions;
    const statusParam = status ? `&status=${status}` : '';
    const needsReviewParam = `&needsReview=${needsReview || false}`;
    const externalStatus = availabilitiesToggle ? `&externalStatus=${availabilitiesToggle}` : '';
    const targetURL = `/property/${recordId}/availabilities?usage=${availabilityType}${statusParam}${externalStatus}${needsReviewParam}`;
    return this.fetchWithPagination(targetURL, paginationSortingOpts);
  }

  getPropertyAvailabilityValues(
    recordId: number,
    availabilitiesOptions: AvailabilitiesFilterOptions
  ): Observable<{ results: any[] }> {
    const { availabilityType, status } = availabilitiesOptions;
    const targetURL = `/property/${recordId}/availabilities?view=brokerAvailabilitySummary&status=${status}&usage=${availabilityType}`;
    return this.httpGet(targetURL);
  }

  /**
   * Fetches the list of possible property discrepancies vs their associated availabilities,
   * the items in the list contains property id, name, address discrepancy count by
   * usage/property type among other fields
   *
   * @param queueFilters :QueueFilters
   * @returns Observable<any[]>
   */
  fetchQueuePropertySyncList(
    entity: string,
    queueFilters: any[],
    paginationOpts: { limit: number; offset: number; sort: { field: string; order: string | number; dataType: any } }
  ): Observable<{ totalCount: number; results: any[] }> {
    return this.httpPost('/queue/search', { filters: queueFilters, entity, ...paginationOpts });
  }

  /**
   * It makes sorting & pagination params and fetches targetUrl with them
   * @param targetURL :string
   * @param paginationOpts :PaginationSorting
   */
  private fetchWithPagination(targetURL: string, paginationOpts: PaginationSorting) {
    const sortingParams = this.makeSortingParams(paginationOpts);
    const paginationParams = this.makePaginationParams(paginationOpts);
    return this.httpGet(`${targetURL}${paginationParams}${sortingParams}`);
  }

  /**
   * it return formatted string with limit & offset if both are passed in params;
   * else it returns empty string
   * @param offset :number
   * @param limit :number
   */
  private makePaginationParams({ offset = 0, limit = 10 }: PaginationSorting): string {
    return limit != null && offset != null ? `&limit=${limit}&offset=${offset}` : '';
  }

  /**
   * it return formatted string with sortField, sortFieltType & sortOrder all three are passed in params;
   *  else it returns empty string
   * @param sortField :string
   * @param sortFieltType :string
   * @param sortOrder :string 'asc' | 'desc'
   */
  private makeSortingParams({ sortField = null, sortFieldType = null, sortOrder = null }: PaginationSorting): string {
    return sortField && sortFieldType && sortOrder
      ? `&sortField=${sortField}&sortFieldType=${sortFieldType}&sortOrder=${sortOrder}`
      : '';
  }

  /**
   * Fetches all property computed areas to find any mismatches
   * @param recordId: property id
   */
  getPropertyAvailabilityDeltas(
    recordId: number
  ): Observable<{ totalCount: number; results: Array<{ [key: string]: any }> }> {
    return this.httpGet(`/property/${recordId}/stats`);
  }

  /**
   * Overrides the discrepancy count value in queues table row for specified usage type
   * @param recordId: property id
   * @param availabilityType: Property usage type
   * @param discrepancyCount: number
   */
  updatePropertyTypeDiscrepancyCount(
    recordId: number,
    availabilityType: string,
    discrepancyCount: number
  ): Observable<{ totalCount: number; results: any[] }> {
    const targetURL = `/property/${recordId}/discrepancyCount?usage=${availabilityType}`;
    return this.httpPut(`${targetURL}`, { discrepancyCount });
  }

  refreshDiscrepancyCount(flag) {
    this.refreshDiscrepancyCount$.next(flag);
  }

  fetchAvailabilityCalculations(propertyId: string) {
    return this.httpGet(`/property/${propertyId}/avail-calc-diff`);
  }

  checkAvailabilityCalculationByAttribute(
    propertyId: number,
    attrObj: AvailabilityCalculationItem
  ): Observable<AvailabilityCalculationItem> {
    return this.httpPost(`/property/${propertyId}/avail-calc-diff`, attrObj);
  }

  getPropertyCurrentSnapshot(propertyId: number): Observable<any[]> {
    return this.httpGet(`/report-stats/property/current-snapshot/qtd?property=${propertyId}`);
  }

  getCalculatedCurrentAvailabilityBlocks(propertyId: number, usageTypes: string[]): Observable<Record<string, any>> {
    return this.httpGet(`/property/${propertyId}/availability-blocks?usageTypes=${usageTypes.join(',')}`);
  }

  applyAvailBlockChanges(propertyId, payload: Record<string, any>): Observable<Record<string, any>> {
    return this.httpPost(`/property/${propertyId}/availability-blocks/apply`, payload);
  }
}
